<script setup lang="ts">
import type { NavigationMenuItem } from "@nuxt/ui";
import Logo from "@/assets/images/logo.svg?component";
import { safeRedirectPath } from "@/utils/auth";

const { t } = useI18n();
const route = useRoute();
const localePath = useLocalePath();
const getRouteBaseName = useRouteBaseName();
const { loggedIn } = useUserSession();
const { show: showLicenseModal } = useLicenseModal();

const menuOpen = ref(false);

const itemsDef: { label: string; routeName: string; authenticated?: boolean }[] = [
  { label: t("nav.sites"), routeName: "sites" },
  { label: t("nav.species"), routeName: "species" },
  { label: t("nav.observations"), routeName: "observations" },
  { label: t("nav.structures"), routeName: "structures" },
  { label: t("activities.title"), routeName: "activities", authenticated: true },
  { label: t("nav.profile"), routeName: "profile", authenticated: true },
];

const isActive = (routeName: string) => {
  const name = String(getRouteBaseName(route) ?? "");
  return name === routeName || name.startsWith(`${routeName}.`);
};

const toNavItem = (item: (typeof itemsDef)[number]): NavigationMenuItem => ({
  label: item.label,
  to: localePath({ name: item.routeName }),
  active: isActive(item.routeName),
});

const publicItems = computed(() => itemsDef.filter((item) => !item.authenticated).map(toNavItem));
const authenticatedItems = computed(() => itemsDef.filter((item) => item.authenticated).map(toNavItem));

const loginTo = computed(() => {
  const redirect = safeRedirectPath(route.fullPath);
  return redirect ? localePath({ path: "/login", query: { redirect } }) : localePath("/login");
});
</script>

<template>
  <UHeader v-model:open="menuOpen" :title="$t('app.title')" :menu="{ side: 'left' }" toggle-side="right" mode="slideover">
    <template #toggle="{ open, toggle, ui }">
      <UButton
        square
        variant="ghost"
        :aria-label="$t('nav.menu')"
        :aria-expanded="open"
        aria-haspopup="menu"
        :class="ui.toggle({ toggleSide: 'right' })"
        @click="toggle"
        class="-mr-2.5"
      >
        <UIcon :name="open ? 'icon:close-large' : 'icon:menu'" class="size-10" aria-hidden="true" />
      </UButton>
    </template>

    <template #left>
      <div class="flex items-center">
        <UButton square :to="$localePath('/')" :aria-label="$t('app.title')" class="rounded-full size-12 justify-center">
          <Logo class="w-5.75" aria-hidden="true" />
        </UButton>
      </div>
      <UNavigationMenu :items="publicItems" variant="link" class="hidden lg:flex" />
    </template>

    <template #body>
      <div class="flex flex-col h-full justify-between">
        <UNavigationMenu :items="publicItems" orientation="vertical" variant="link" />
        <UNavigationMenu v-if="loggedIn" :items="authenticatedItems" orientation="vertical" variant="link" class="mt-auto" />
        <div class="flex items-center gap-6 mt-8">
          <template v-if="!loggedIn">
            <UButton :to="loginTo" variant="solid" color="secondary" size="md">{{ $t("ui.login") }}</UButton>
            <UButton :to="$localePath('/register')" variant="link" class="no-underline">
              {{ $t("register.title") }}
            </UButton>
          </template>
          <template v-else>
            <UButton color="secondary" :to="$localePath({ name: 'activity.type' })">
              {{ $t("activity.add") }}
              <template #trailing>
                <UIcon name="icon:plus" class="size-4 -mx-0.5" />
              </template>
            </UButton>
          </template>
        </div>
      </div>
    </template>

    <template #right>
      <div v-if="!loggedIn" class="hidden lg:flex items-center gap-x-5.5">
        <UButton :to="loginTo" variant="solid" color="secondary" size="md">{{ $t("ui.login") }}</UButton>
        <UButton :to="$localePath('/register')" variant="link" size="md" class="no-underline">{{ $t("register.title") }}</UButton>
      </div>
      <div v-else class="flex gap-x-4 lg:gap-x-3">
        <UNavigationMenu :items="authenticatedItems" variant="link" class="hidden lg:flex" />
        <div v-if="!menuOpen" class="flex gap-x-4 lg:gap-x-3 items-center">
          <UButton
            :to="$localePath({ name: 'activity.type' })"
            variant="outline"
            color="primary"
            :aria-label="$t('activity.add')"
            data-app-tutorial-target="activity-create"
            class="size-12 lg:size-8 rounded-full p-0 flex items-center justify-center"
          >
            <UIcon name="icon:plus-lg" class="size-8 lg:size-[1.334rem]" />
          </UButton>
          <UButton
            variant="outline"
            color="primary"
            :aria-label="$t('license.title')"
            data-app-tutorial-target="license"
            class="lg:hidden rounded-full size-12 p-0 flex items-center justify-center"
            @click="showLicenseModal"
          >
            <UIcon name="icon:qr" class="size-5.5" />
          </UButton>
        </div>
      </div>
    </template>
  </UHeader>
</template>
